import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { authService, tareasService } from '../services/api';
import HtmlContent from '../components/HtmlContent';
import CodigoEntregaViewer from '../components/CodigoEntregaViewer';
import { formatEntregaAlumno } from '../utils/formatEntregaAlumno';
import BbcodeContent from '../components/BbcodeContent';
import RichTextEditor from '../components/RichTextEditor';
import ConfirmPopup from '../components/ConfirmPopup';
import './Tareas.css';

/** Lenguaje Prism para el highlighter (html -> markup). */
const getPrismLanguage = (lang) => {
  if (!lang || lang === 'plaintext') return 'plaintext';
  return lang === 'html' ? 'markup' : lang;
};

const CorreccionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entregaId } = useParams();
  const [entrega, setEntrega] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [nota, setNota] = useState('');
  const [revisado, setRevisado] = useState(false);
  const [devolucion, setDevolucion] = useState('');
  const [pedirReentrega, setPedirReentrega] = useState(false);
  const [nuevaFechaCierre, setNuevaFechaCierre] = useState('');
  const [yaCorregidaMsg, setYaCorregidaMsg] = useState('');

  const backState = useMemo(() => {
    const ret = location.state?.returnTo || {};
    return {
      tab: 'correccion',
      subTab: ret.subTab === 'corregidas' ? 'corregidas' : 'pendientes',
      ...(typeof ret.page === 'number' && ret.page >= 1 ? { page: ret.page } : {}),
      ...(ret.cursoFiltro !== undefined && ret.cursoFiltro !== null
        ? { cursoFiltro: ret.cursoFiltro }
        : {}),
    };
  }, [location.state]);

  const volverATareas = (extra = {}) => {
    navigate('/dashboard/tareas', { state: { ...backState, ...extra } });
  };

  const volverTrasYaCorregida = () => {
    setYaCorregidaMsg('');
    volverATareas({ subTab: 'pendientes', refreshPendientes: true });
  };

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      setError('');
      setYaCorregidaMsg('');
      try {
        const data = await tareasService.getEntregaParaCorregir(entregaId, { modo: 'corregir' });
        setEntrega(data);
        if (data.nota != null && data.nota !== '') setNota(String(data.nota));
        if (data.revisado) setRevisado(true);
        setDevolucion(data.devolucion || '');
        setPedirReentrega(Boolean(data.pedir_reentrega));
        if (data.nueva_fecha_cierre) setNuevaFechaCierre(data.nueva_fecha_cierre.slice(0, 10));
      } catch (err) {
        const data = err.response?.data || {};
        if (err.response?.status === 409 || data.code === 'ya_corregida') {
          setYaCorregidaMsg(data.error || 'Esta entrega ya fue corregida.');
          setEntrega(null);
        } else {
          setError(data.error || 'Error al cargar la entrega');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entregaId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!entrega) return;
    setError('');
    const seCalifica = entrega.se_califica;
    if (seCalifica && !pedirReentrega) {
      if (nota.trim() === '') {
        setError('La nota es obligatoria.');
        return;
      }
      const n = parseFloat(nota);
      if (isNaN(n) || n < 0 || n > 10) {
        setError('La nota debe ser un número entre 0 y 10.');
        return;
      }
    }
    if (pedirReentrega && !nuevaFechaCierre.trim()) {
      setError('Debe indicar la nueva fecha de cierre para la reentrega.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...(seCalifica && !pedirReentrega ? { nota: nota === '' ? null : parseFloat(nota) } : !seCalifica ? { revisado } : {}),
        devolucion: devolucion.trim() || '',
        pedir_reentrega: pedirReentrega,
        ...(pedirReentrega ? { nueva_fecha_cierre: nuevaFechaCierre.trim() } : {}),
      };
      await tareasService.corregirEntrega(entrega.id, payload);
      volverATareas();
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 || data.code === 'ya_corregida') {
        setYaCorregidaMsg(data.error || 'Esta entrega ya fue corregida.');
      } else {
        setError(data.error || 'Error al guardar la corrección');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const descargarAdjuntoEntrega = async (a) => {
    try {
      await tareasService.descargarArchivoEntrega(a.id, a.nombre_original || 'archivo');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo descargar el archivo');
    }
  };

  if (loading) {
    return (
      <div className="tareas-loading">
        <div className="loading-spinner"></div>
        <p>Cargando entrega...</p>
      </div>
    );
  }

  if (yaCorregidaMsg && !entrega) {
    return (
      <div className="tareas-container">
        <ConfirmPopup
          open
          title="Entrega ya corregida"
          message={yaCorregidaMsg}
          confirmLabel="Aceptar"
          cancelLabel={null}
          onConfirm={volverTrasYaCorregida}
          onCancel={volverTrasYaCorregida}
          variant="primary"
        />
      </div>
    );
  }

  if (!entrega) {
    return (
      <div className="tareas-container">
        <div className="tareas-error">{error || 'Entrega no encontrada'}</div>
        <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => volverATareas()}>
          Volver a Tareas
        </button>
      </div>
    );
  }

  let contenido = {};
  try {
    contenido = typeof entrega.comentario === 'string' ? JSON.parse(entrega.comentario || '{}') : (entrega.comentario || {});
  } catch (_) {}
  const codigoLenguaje = getPrismLanguage(contenido.codigoLenguaje);

  const archivos = entrega.archivos_list || [];
  const seCalifica = entrega.se_califica;
  const textoLooksLikeHtml = contenido.texto && /<[a-z][\s\S]*>/i.test(String(contenido.texto));

  return (
    <div className="tareas-container">
      <div className="tareas-form-page-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Corregir entrega</h1>
        <button
          type="button"
          className="tareas-btn tareas-btn-secondary"
          onClick={() => volverATareas()}
        >
          ← Volver
        </button>
      </div>
      <div className="tareas-correccion-info">
        <p><strong>Alumno:</strong> {formatEntregaAlumno(entrega)}</p>
        <p><strong>Curso:</strong> {entrega.curso_nombre || '-'}</p>
        <p><strong>Tarea:</strong> {entrega.tarea_titulo || '-'}</p>
      </div>
      {entrega.tarea_descripcion && (
        <div className="tareas-correccion-descripcion">
          <strong>Descripción de la tarea:</strong>
          <HtmlContent content={entrega.tarea_descripcion} className="tareas-descripcion-text" />
        </div>
      )}
      <div className="tareas-correccion-contenido">
        <strong>Contenido de la entrega</strong>
        {contenido.texto && (
          <div className="tareas-correccion-campo">
            <label>Texto:</label>
            <div className="tareas-correccion-valor tareas-correccion-texto-bbcode">
              {textoLooksLikeHtml ? (
                <HtmlContent content={contenido.texto} tag="div" />
              ) : (
                <BbcodeContent content={contenido.texto} tag="div" />
              )}
            </div>
          </div>
        )}
        {contenido.enlace && (
          <div className="tareas-correccion-campo">
            <label>Enlace:</label>
            <a href={contenido.enlace} target="_blank" rel="noopener noreferrer">{contenido.enlace}</a>
          </div>
        )}
        {contenido.codigo && (
          <div className="tareas-correccion-campo">
            <label>Código:</label>
            <CodigoEntregaViewer codigo={contenido.codigo} language={codigoLenguaje} />
          </div>
        )}
        {archivos.length > 0 && (
          <div className="tareas-correccion-campo tareas-correccion-archivos">
            <label>Archivos adjuntos</label>
            <div className="tareas-correccion-archivos-grid">
              {archivos.map((a) => (
                <div key={a.id} className="tareas-correccion-archivo-card">
                  <span className="tareas-correccion-archivo-icono" aria-hidden>📎</span>
                  <span className="tareas-correccion-archivo-nombre" title={a.nombre_original || 'Archivo'}>
                    {a.nombre_original || 'Archivo'}
                  </span>
                  <button
                    type="button"
                    className="tareas-correccion-archivo-descargar"
                    onClick={() => descargarAdjuntoEntrega(a)}
                  >
                    Descargar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {!contenido.texto && !contenido.enlace && !contenido.codigo && archivos.length === 0 && (
          <p className="tareas-correccion-sin-contenido">Sin contenido adicional.</p>
        )}
      </div>

      {Array.isArray(entrega.historial) && entrega.historial.length > 0 && (
        <div className="ver-correccion-historial">
          <h2 className="ver-correccion-historial-titulo">Entregas anteriores</h2>
          {entrega.historial.map((item, index) => {
            let cont = {};
            try {
              cont = typeof item.comentario === 'string' ? JSON.parse(item.comentario || '{}') : (item.comentario || {});
            } catch (_) {}
            const codigoLang = getPrismLanguage(cont.codigoLenguaje);
            const archivosItem = item.archivos_list || [];
            const textoHtml = cont.texto && /<[a-z][\s\S]*>/i.test(String(cont.texto));
            return (
              <div key={item.id} className="ver-correccion-historial-item">
                <h3 className="ver-correccion-historial-item-titulo">
                  Entrega anterior {entrega.historial.length > 1 ? `#${index + 1}` : ''}
                  {item.fecha_entrega && (
                    <span className="ver-correccion-historial-fecha">
                      {' — '}
                      {new Date(item.fecha_entrega).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  )}
                </h3>
                <div className="tareas-correccion-contenido ver-correccion-historial-contenido">
                  {cont.texto && (
                    <div className="tareas-correccion-campo">
                      <label>Texto:</label>
                      <div className="tareas-correccion-valor tareas-correccion-texto-bbcode">
                        {textoHtml ? (
                          <HtmlContent content={cont.texto} tag="div" />
                        ) : (
                          <BbcodeContent content={cont.texto} tag="div" />
                        )}
                      </div>
                    </div>
                  )}
                  {cont.enlace && (
                    <div className="tareas-correccion-campo">
                      <label>Enlace:</label>
                      <a href={cont.enlace} target="_blank" rel="noopener noreferrer">{cont.enlace}</a>
                    </div>
                  )}
                  {cont.codigo && (
                    <div className="tareas-correccion-campo">
                      <label>Código:</label>
                      <CodigoEntregaViewer codigo={cont.codigo} language={codigoLang} />
                    </div>
                  )}
                  {archivosItem.length > 0 && (
                    <div className="tareas-correccion-campo tareas-correccion-archivos">
                      <label>Archivos adjuntos</label>
                      <div className="tareas-correccion-archivos-grid">
                        {archivosItem.map((a) => (
                          <div key={a.id} className="tareas-correccion-archivo-card">
                            <span className="tareas-correccion-archivo-icono" aria-hidden>📎</span>
                            <span className="tareas-correccion-archivo-nombre" title={a.nombre_original || 'Archivo'}>
                              {a.nombre_original || 'Archivo'}
                            </span>
                            <button
                              type="button"
                              className="tareas-correccion-archivo-descargar"
                              onClick={() => descargarAdjuntoEntrega(a)}
                            >
                              Descargar
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!cont.texto && !cont.enlace && !cont.codigo && archivosItem.length === 0 && (
                    <p className="tareas-correccion-sin-contenido">Sin contenido adicional.</p>
                  )}
                </div>
                <div className="tareas-correccion-contenido ver-correccion-resultado ver-correccion-historial-resultado">
                  <div className="tareas-correccion-info ver-correccion-meta">
                    {item.nota != null && <p><strong>Nota:</strong> {String(item.nota)}</p>}
                    <p><strong>Estado:</strong> {item.estado || '-'}</p>
                    {item.fecha_revision && (
                      <p><strong>Fecha de corrección:</strong> {new Date(item.fecha_revision).toLocaleString('es-AR')}</p>
                    )}
                    {item.corregido_por_nombre && (
                      <p><strong>Corrigió:</strong> {item.corregido_por_nombre}</p>
                    )}
                  </div>
                  {item.devolucion && (
                    <div className="tareas-correccion-campo">
                      <label>Devolución al estudiante:</label>
                      <div className="ver-correccion-devolucion">
                        <HtmlContent content={item.devolucion} tag="div" className="html-content" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <ConfirmPopup
        open={Boolean(yaCorregidaMsg) && Boolean(entrega)}
        title="Entrega ya corregida"
        message={yaCorregidaMsg}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={volverTrasYaCorregida}
        onCancel={volverTrasYaCorregida}
        variant="primary"
      />
      <form onSubmit={handleSubmit} className="tareas-form correccion-form">
        <div className="tareas-form-group">
          <label htmlFor="correccion-devolucion">Devolución al estudiante</label>
          <RichTextEditor
            value={devolucion}
            onChange={setDevolucion}
            placeholder="Escriba la devolución al estudiante..."
            minHeight="140px"
            allowImageUpload
          />
        </div>
        <div className="tareas-form-group ver-correccion-anular">
          <div className="ver-correccion-anular-row">
            <input
              id="correccion-pedir-reentrega"
              type="checkbox"
              checked={pedirReentrega}
              onChange={(e) => setPedirReentrega(e.target.checked)}
            />
            <span className="ver-correccion-anular-titulo">Pedir reentrega</span>
          </div>
        </div>
        {pedirReentrega && (
          <div className="tareas-form-group">
            <label htmlFor="correccion-nueva-fecha">Nueva fecha de cierre para la reentrega *</label>
            <input
              id="correccion-nueva-fecha"
              type="date"
              value={nuevaFechaCierre}
              onChange={(e) => setNuevaFechaCierre(e.target.value)}
              className="tareas-input ver-correccion-fecha-input"
              required
            />
          </div>
        )}
        {seCalifica && !pedirReentrega ? (
          <div className="tareas-form-group">
            <label htmlFor="correccion-nota">Nota (0-10) *</label>
            <input
              id="correccion-nota"
              type="number"
              min="0"
              max="10"
              step="0.01"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              className="tareas-input"
              required
            />
          </div>
        ) : !seCalifica ? (
          <div className="tareas-form-group ver-correccion-anular">
            <div className="ver-correccion-anular-row">
              <input
                id="correccion-revisado"
                type="checkbox"
                checked={revisado}
                onChange={(e) => setRevisado(e.target.checked)}
              />
              <span className="ver-correccion-anular-titulo">Marcar como revisado</span>
            </div>
          </div>
        ) : null}
        <div className="tareas-form-actions">
          <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => volverATareas()}>
            Cancelar
          </button>
          <button type="submit" className="tareas-btn tareas-btn-primary" disabled={submitting}>
            {submitting ? 'Guardando...' : 'Guardar corrección'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CorreccionForm;
